// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import './styles/carbon-entry.scss';
import '@carbon/charts/styles.css';
import './index.css';
import App from './App';
import { msalInstance } from './config/authConfig';
import reportWebVitals from './reportWebVitals';

/**
 * Bootstrap MSAL before rendering.
 *
 * 1. `handleRedirectPromise()` processes any CIAM redirect response
 *    (auth code in the URL) so the app has tokens before React renders.
 * 2. Set the active account so MSAL knows which identity to use for
 *    silent token acquisition.
 */
async function bootstrap() {
  // Process CIAM redirect response (no-op if this isn't a redirect return)
  await msalInstance.initialize();
  const response = await msalInstance.handleRedirectPromise();

  if (response?.account) {
    msalInstance.setActiveAccount(response.account);
  } else if (!msalInstance.getActiveAccount()) {
    // Pick the first account if one exists in cache (e.g. page refresh)
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Keep active account in sync when login succeeds in other tabs/popups
  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      if (result.account) {
        msalInstance.setActiveAccount(result.account);
      }
    }
  });

  const root = ReactDOM.createRoot(document.getElementById('root')!);
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>
  );
}

bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
